import {MOON_250} from '@wandb/weave/common/css/color.styles';
import {Button} from '@wandb/weave/components/Button';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {Panel2Loader} from '@wandb/weave/components/Panel2/PanelComp';
import {
  constBoolean,
  constFunction,
  constNumber,
  constString,
  constTimestamp,
  isAssignableTo,
  listObjectType,
  ListType,
  maybe,
  Node,
  opArray,
  opArtifactMembershipArtifactAliases,
  opArtifactMembershipArtifactVersion,
  opArtifactMemberships,
  opArtifactMembershipVersionIndex,
  opArtifactVersionCreatedAt,
  opArtifactVersionCreatedBy,
  opArtifactVersionCreatedByUser,
  opArtifactVersionIsGenerated,
  opArtifactVersionMetadata,
  opArtifactVersionRawTags,
  opArtifactVersionRunHistoryRow,
  opArtifactVersionSize,
  opArtifactVersionTTLDurationSeconds,
  opArtifactVersionUsedBy,
  opBooleanEqual,
  opCount,
  opDatetimeAddMs,
  opLimit,
  opNoneCoalesce,
  opNumberEqual,
  opNumberMult,
  opNumberToByteString,
  opPick,
  opRunLink,
  opSort,
  opStringReplace,
  opTimestampMax,
  opTimestampRelativeStringAutoFormat,
  opUserLink,
  OutputNode,
  typedDictPropertyTypes,
  varNode,
  weaveIf,
} from '@wandb/weave/core';
import {useNodeWithServerType} from '@wandb/weave/react';
import _ from 'lodash';
import React, {useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {QuickPanelWBReactTable} from '../../../components/Panel2/PanelWBReactTable';

const HistoryTableWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  width: 100%;
  margin-top: 20px;
  height: 400px;
  flex-direction: column;
  overflow-y: auto;
  border: 1px solid ${MOON_250};
  border-radius: 4px;
  .wb-react-table {
    border: none;
  }
  scrollbar-width: none;
`;
HistoryTableWrapper.displayName = 'S.HistoryTableWrapper';

const ToggleWrapper = styled.div`
  margin: 8px;
  margin-left: auto;
`;
ToggleWrapper.displayName = 'S.ToggleWrapper';

const inputType = 'artifact' as const;
export type ConfigType = {};

export const PanelArtifactHistory: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & {
    onRowClick?: (index: number, node: Node) => void;
    artifactRegistry?: boolean;
  }
> = props => {
  const [isHistoryMetricsOn, setIsHistoryMetricsOn] = useState(false);
  const onToggleHistoryMetrics = () => {
    setIsHistoryMetricsOn(!isHistoryMetricsOn);
  };
  const tableNode = React.useMemo(() => {
    return opSort({
      arr: opArtifactMemberships({
        artifact: props.input,
      }),
      compFn: constFunction({row: 'artifactMembership'}, ({row}) => {
        return opArray({
          0: opArtifactMembershipVersionIndex({
            artifactMembership: row,
          }),
        } as any);
      }),
      columnDirs: opArray({
        0: constString('desc'),
      } as any),
    });
  }, [props.input]) as OutputNode<ListType<'any'>>;

  // Adding a limit here so we don't look at all the runs for all the artifacts greedily
  // TODO: Use the table pagination to select the columns
  const viewableTableNode = React.useMemo(
    () =>
      opLimit({
        arr: tableNode,
        limit: constNumber(10),
      }),
    [tableNode]
  );

  const tableColumns = React.useMemo(() => {
    return artifactVersionTableColumns(tableNode, props.artifactRegistry);
  }, [tableNode, props.artifactRegistry]);

  const metadataTypeNode = useNodeWithServerType(
    opArtifactVersionMetadata({
      artifactVersion: opArtifactMembershipArtifactVersion({
        artifactMembership: viewableTableNode,
      }),
    })
  );

  const metadataKeys = React.useMemo(() => {
    if (metadataTypeNode.loading) {
      return [];
    } else {
      try {
        return Object.keys(
          typedDictPropertyTypes(listObjectType(metadataTypeNode.result.type))
        );
      } catch (e) {
        return [];
      }
    }
  }, [metadataTypeNode]);

  const tableColumnsWithMetadata = React.useMemo(() => {
    const newCols = metadataKeys
      .filter(key => !key.startsWith('_'))
      .map(key => {
        return {
          name: `m.${key}`,
          selectFn: opPick({
            obj: opArtifactVersionMetadata({
              artifactVersion: opArtifactMembershipArtifactVersion({
                artifactMembership: varNode('artifactMembership', 'row'),
              }),
            }),
            // Add a backslash to the dot in key to avoid the dot to be parsed as nested keys
            key: constString(key.replace(/\./g, '\\.')),
          }),
        };
      });
    return [...tableColumns, ...newCols];
  }, [tableColumns, metadataKeys]);

  const historyMetricsNode = React.useMemo(() => {
    return opArtifactVersionRunHistoryRow({
      artifactVersion: opArtifactMembershipArtifactVersion({
        artifactMembership: viewableTableNode,
      }),
    });
  }, [viewableTableNode]);

  const historyMetricsNodeTyped = useNodeWithServerType(historyMetricsNode);

  const historyMetricsKeys = React.useMemo(() => {
    if (historyMetricsNodeTyped.loading) {
      return [];
    }
    const historyKeyTypes = typedDictPropertyTypes(
      listObjectType(historyMetricsNodeTyped.result.type)
    );

    return Object.keys(
      _.pickBy(
        historyKeyTypes,
        (val, key) =>
          !key.startsWith('_') && isAssignableTo(val, maybe('number'))
      )
    );
  }, [historyMetricsNodeTyped]);

  const tableColumnsWithHistory = React.useMemo(() => {
    if (!isHistoryMetricsOn) {
      return tableColumnsWithMetadata;
    }
    const newCols = historyMetricsKeys.map(key => {
      return {
        name: `h.${key}`,
        selectFn: opPick({
          obj: opArtifactVersionRunHistoryRow({
            artifactVersion: opArtifactMembershipArtifactVersion({
              artifactMembership: varNode('artifactMembership', 'row'),
            }),
          }),
          key: constString(key),
        }),
      };
    });
    return [...tableColumnsWithMetadata, ...newCols];
  }, [tableColumnsWithMetadata, historyMetricsKeys, isHistoryMetricsOn]);

  if (metadataTypeNode.loading || historyMetricsNodeTyped.loading) {
    return (
      <HistoryTableWrapper>
        <Panel2Loader />
      </HistoryTableWrapper>
    );
  }
  return (
    <HistoryTableWrapper>
      <ToggleWrapper>
        <Button
          size="small"
          variant="secondary"
          icon={isHistoryMetricsOn ? 'hide-hidden' : 'show-visible'}
          onClick={onToggleHistoryMetrics}
          tooltip="The run metrics at the time of logging the artifact">
          {isHistoryMetricsOn ? 'Hide run metrics' : 'Show run metrics'}
        </Button>
      </ToggleWrapper>
      <QuickPanelWBReactTable
        input={tableNode}
        columns={tableColumnsWithHistory}
        onRowClick={props.onRowClick}
      />
    </HistoryTableWrapper>
  );
};

const currentTime = new Date().getTime();

function artifactVersionTableColumns(
  tableNode: OutputNode<ListType<'any'>>,
  artifactRegistry?: boolean
) {
  const rowType = listObjectType(tableNode.type);
  const artifactVersionNode = opArtifactMembershipArtifactVersion({
    artifactMembership: varNode(rowType, 'row'),
  });
  const cols = [
    {
      name: 'Version',
      selectFn: opArtifactMembershipVersionIndex({
        artifactMembership: varNode(rowType, 'row'),
      }),
    },
    {
      name: 'Aliases',
      selectFn: opArtifactMembershipArtifactAliases({
        artifactMembership: varNode(rowType, 'row'),
      }),
    },
    {
      name: 'Tags',
      selectFn: opArtifactVersionRawTags({
        artifactVersion: artifactVersionNode,
      }),
      panelDef: {panelID: 'artifactVersionTags'},
    },
    {
      name: 'Created',
      selectFn: opArtifactVersionCreatedAt({
        artifactVersion: artifactVersionNode,
      }),
      panelDef: {panelID: 'date', panelConfig: {format: 'MMMM Do, YYYY'}},
    },
    {
      name: 'TTL Remaining',
      selectFn: weaveIf(
        opBooleanEqual({
          lhs: opArtifactVersionIsGenerated({
            artifactVersion: artifactVersionNode,
          }),
          rhs: constBoolean(true),
        }),
        constString('N/A'),
        weaveIf(
          opNumberEqual({
            lhs: opArtifactVersionTTLDurationSeconds({
              artifactVersion: artifactVersionNode,
            }),
            rhs: constNumber(-2),
          }),
          constString('Inactive'),
          opStringReplace({
            str: opTimestampRelativeStringAutoFormat({
              lhs: opTimestampMax({
                timestamps: opArray({
                  0: opDatetimeAddMs({
                    lhs: opArtifactVersionCreatedAt({
                      artifactVersion: artifactVersionNode,
                    }),
                    rhs: opNumberMult({
                      lhs: opArtifactVersionTTLDurationSeconds({
                        artifactVersion: artifactVersionNode,
                      }),
                      rhs: constNumber(1000),
                    }),
                  }),
                  1: constTimestamp(currentTime),
                } as any),
              }),
              rhs: constTimestamp(currentTime),
            }),
            sub: constString('less than 1 ms'),
            newSub: constString('Pending deletion'),
          })
        )
      ),
    },
    {
      name: '# of Consuming Runs',
      selectFn: opCount({
        arr: opArtifactVersionUsedBy({
          artifactVersion: artifactVersionNode,
        }),
      }),
    },
    {
      name: 'Size',
      selectFn: opNumberToByteString({
        in: opArtifactVersionSize({
          artifactVersion: artifactVersionNode,
        }),
      }),
    },
  ];
  if (!artifactRegistry) {
    // TODO: This is a good example of a missing part of Weave.
    // We can't realistically make an op that returns a union of user
    // and run, and then make meaningful ops downstream. We need to
    // switch on the type somehow.
    // TODO(ishita): add back createdByLink once we've figured out how to
    // deal with auth issues around run project/entity for linked artifacts
    cols.splice(2, 0, {
      name: 'Logged By',
      selectFn: opNoneCoalesce({
        lhs: opRunLink({
          run: opArtifactVersionCreatedBy({
            artifactVersion: artifactVersionNode,
          }),
        }),
        rhs: opUserLink({
          user: opArtifactVersionCreatedByUser({
            artifactVersion: artifactVersionNode,
          }),
        }),
      }),
    });
  }
  return cols;
}
